import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileSpreadsheet, Printer, CheckCircle2, AlertCircle, Play, Download, BookOpen } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { generateBalanceSheetPDF } from '../../../utils/pdfExport';
import ReportWorkflowTrail from '../../reports/ReportWorkflowTrail';
import ReportPeriodSelector, { REPORT_MONTHS, getMonthEndDate } from '../../reports/ReportPeriodSelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function BalanceSheetTab({ fmt, viewingStudentId, viewingStudentName, onTraceGl }) {
    const now = new Date();
    const initialMonth = now.getMonth() + 1;
    const initialYear = now.getFullYear();
    const initialAsOfDate = getMonthEndDate(initialYear, initialMonth);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [data, setData] = useState(null);
    const [reportType, setReportType] = useState('as_of'); // as_of, comparative
    const [dateMode, setDateMode] = useState('single'); // single, range
    const [compareType] = useState('prev_month'); // prev_month, dec_last_year, same_month_last_year
    const [periodMode, setPeriodMode] = useState('monthly');
    const [withZero, setWithZero] = useState(false);
    
    const [filters, setFilters] = useState({
        date: initialAsOfDate,
        dateFrom: '',
        dateTo: initialAsOfDate,
        month: initialMonth,
        year: initialYear,
        company: '',
        branch: ''
    });
    
    const months = REPORT_MONTHS;
    const pad = (value) => String(value).padStart(2, '0');
    const lastDayOfMonth = (year, month) => new Date(year, month, 0).getDate();

    const applyPeriodMode = (mode) => {
        setPeriodMode(mode);
        setReportType('as_of');
        setDateMode('single');
        setFilters(prev => {
            if (mode === 'monthly') {
                const date = getMonthEndDate(prev.year, prev.month);
                return { ...prev, date, dateTo: date };
            }
            if (mode === 'annual') {
                const date = `${prev.year}-12-31`;
                return { ...prev, date, dateTo: date };
            }
            return { ...prev, date: prev.date || initialAsOfDate, dateTo: prev.date || initialAsOfDate };
        });
    };

    const handlePeriodMonthChange = (month) => {
        setPeriodMode('monthly');
        setReportType('as_of');
        setDateMode('single');
        setFilters(prev => {
            const date = getMonthEndDate(prev.year, month);
            return { ...prev, month, date, dateTo: date };
        });
    };

    const handlePeriodYearChange = (year) => {
        setReportType('as_of');
        setDateMode('single');
        setFilters(prev => {
            const date = periodMode === 'annual' ? `${year}-12-31` : getMonthEndDate(year, prev.month);
            return { ...prev, year, date, dateTo: date };
        });
    };

    const handleCustomAsOfDateChange = (value) => {
        setPeriodMode('custom');
        setReportType('as_of');
        setDateMode('single');
        setFilters(prev => ({ ...prev, date: value, dateTo: value }));
    };

    const getTraceRange = () => {
        if (data?.as_of_date) {
            return { dateFrom: '', dateTo: data.as_of_date };
        }
        if (reportType === 'comparative') {
            const dateTo = `${filters.year}-${pad(filters.month)}-${pad(lastDayOfMonth(filters.year, filters.month))}`;
            return { dateFrom: '', dateTo };
        }
        if (dateMode === 'range') {
            return { dateFrom: filters.dateFrom || '', dateTo: filters.dateTo || filters.date || '' };
        }
        return { dateFrom: '', dateTo: filters.date };
    };

    const accountRangeFromItems = (items = []) => {
        const codes = [...new Set(items.map(item => String(item.account_code || '').trim()).filter(Boolean))].sort();
        if (codes.length === 0) return null;
        return {
            from: codes[0],
            to: codes[codes.length - 1],
            label: codes.length === 1 ? codes[0] : `${codes[0]} to ${codes[codes.length - 1]}`,
        };
    };

    const traceSectionInGl = (label, items, amount) => {
        const range = accountRangeFromItems(items);
        if (!range) {
            toast.info(`No posted ${label.toLowerCase()} accounts to trace for this report.`);
            return;
        }
        const { dateFrom, dateTo } = getTraceRange();
        onTraceGl?.({
            sourceReport: 'Balance Sheet',
            label,
            accountFrom: range.from,
            accountTo: range.to,
            dateFrom,
            dateTo,
            amount,
            accountLabel: range.label,
            scopeLabel: viewingStudentName || 'Your workspace',
            nextAction: 'Review the filtered posted GL movement, then use Source Ref to open the original JV, Opening Balance, AR/AP, or Bank Recon evidence.',
        });
    };

    const traceAccountInGl = (item, sectionLabel) => {
        const accountCode = String(item?.account_code || '').trim();
        if (!accountCode) {
            toast.info('This account has no account code to trace in General Ledger.');
            return;
        }
        const { dateFrom, dateTo } = getTraceRange();
        onTraceGl?.({
            sourceReport: 'Balance Sheet',
            label: `${sectionLabel}: ${item.account_name || accountCode}`,
            accountFrom: accountCode,
            accountTo: accountCode,
            dateFrom,
            dateTo,
            amount: item.current_balance ?? 0,
            accountLabel: accountCode,
            scopeLabel: viewingStudentName || 'Your workspace',
            nextAction: 'Review the account-level posted GL movement, then use Source Ref to open the original JV, Opening Balance, AR/AP, or Bank Recon evidence.',
        });
    };
    
    // Process Balance Sheet
    const handleProcess = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const params = new URLSearchParams();
            
            params.append('mode', reportType);
            params.append('with_zero', withZero);
            
            if (reportType === 'as_of') {
                params.append('date_mode', dateMode);
                if (dateMode === 'single') {
                    params.append('date', filters.date);
                } else {
                    params.append('date_from', filters.dateFrom);
                    params.append('date_to', filters.dateTo);
                }
            } else {
                params.append('month', filters.month);
                params.append('year', filters.year);
                params.append('compare_type', compareType);
            }
            
            if (filters.company) params.append('company', filters.company);
            if (filters.branch) params.append('branch', filters.branch);
            if (viewingStudentId) params.append('student_id', viewingStudentId);
            
            const response = await axios.get(`${API}/balance-sheet?${params.toString()}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            setData(response.data);
            toast.success('Balance Sheet generated on this report tab. It uses posted GL balances as of the selected date; source JVs/AR/AP records were not changed.');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to generate Balance Sheet');
        } finally {
            setLoading(false);
        }
    };

    const handleExportPDF = () => {
        if (!data) return;
        setExporting(true);
        try {
            const result = generateBalanceSheetPDF(data, filters, reportType, compareType, fmt);
            if (result.success) {
                toast.success(`PDF exported: ${result.filename} (${(result.blobSize / 1024).toFixed(2)} KB). Export only; source records were not changed.`);
            } else {
                toast.error(`Failed to export PDF: ${result.error}`);
            }
        } catch (error) {
            toast.error('Failed to export PDF: ' + error.message);
        } finally {
            setExporting(false);
        }
    };
    
    // Render section
    const renderSection = (title, items, bgColor, borderColor) => (
        <div className={`border ${borderColor} rounded-lg overflow-hidden mb-4`}>
            <div className={`${bgColor} px-4 py-2 font-semibold text-white`}>
                {title}
            </div>
            <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                    <tr>
                        <th className="text-left px-4 py-2">Account Code</th>
                        <th className="text-left px-4 py-2">Account Name</th>
                        <th className="text-right px-4 py-2">Current Balance</th>
                        <th className="text-right px-4 py-2">Trace</th>
                        {reportType === 'comparative' && (
                            <>
                                <th className="text-right px-4 py-2">Comparison</th>
                                <th className="text-right px-4 py-2">Variance</th>
                            </>
                        )}
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                    {items.length === 0 ? (
                        <tr>
                            <td colSpan={reportType === 'comparative' ? 6 : 4} className="px-4 py-4 text-center text-slate-400">
                                No accounts with balances
                            </td>
                        </tr>
                    ) : (
                        items.map((item) => (
                            <tr key={item.account_code} className="hover:bg-slate-50">
                                <td className="px-4 py-2 font-mono">{item.account_code}</td>
                                <td className="px-4 py-2">{item.account_name}</td>
                                <td className="px-4 py-2 text-right font-mono">
                                    {fmt(item.current_balance ?? 0)}
                                </td>
                                <td className="px-4 py-2 text-right">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => traceAccountInGl(item, title)}
                                        data-testid={`trace-bs-gl-${String(item.account_code || '').replace(/[^a-zA-Z0-9_-]/g, '-')}`}
                                    >
                                        <BookOpen className="w-3.5 h-3.5 mr-1" /> Trace
                                    </Button>
                                </td>
                                {reportType === 'comparative' && (
                                    <>
                                        <td className="px-4 py-2 text-right font-mono text-slate-500">
                                            {fmt(item.comparison_balance || 0)}
                                        </td>
                                        <td className={`px-4 py-2 text-right font-mono ${
                                            (item.variance || 0) > 0 ? 'text-green-600' : 
                                            (item.variance || 0) < 0 ? 'text-red-600' : 'text-slate-400'
                                        }`}>
                                            {item.variance > 0 ? '+' : ''}{fmt(item.variance || 0)}
                                        </td>
                                    </>
                                )}
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
    
    return (
        <div className="space-y-6" data-testid="balance-sheet-tab">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-bold text-slate-900 font-['Manrope']">Balance Sheet</h2>
                    <p className="text-sm text-slate-500">
                        {viewingStudentName ? `Assets = Liabilities + Equity for ${viewingStudentName}` : 'Assets = Liabilities + Equity'}
                    </p>
                </div>
            </div>
            {viewingStudentName && (
                <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900" data-testid="balance-sheet-student-scope">
                    Student review scope is active. This report uses posted asset, liability, and equity GL entries from {viewingStudentName}'s workspace only.
                </div>
            )}
            <ReportWorkflowTrail
                title="Balance Sheet trail"
                testId="balance-sheet-workflow-trail"
                scope={viewingStudentName ? `Reviewing ${viewingStudentName}` : 'Your workspace'}
                items={[
                    {
                        label: 'Source records',
                        value: 'Uses posted source records only: JVs, Opening Balance JVs, AR/AP postings, and bank-recon adjustment JVs through the selected as-of date.'
                    },
                    {
                        label: 'Where it appears',
                        value: 'After posting, the work remains in its source module and appears here through General Ledger and Trial Balance for the same as-of date.'
                    },
                    {
                        label: 'How to verify',
                        value: 'Use Trace on a section or account to open General Ledger, then use Source Ref to open the original evidence.'
                    },
                    {
                        label: 'Next safe action',
                        value: 'If a balance is missing, check the source status first. Draft, saved, submitted, and returned work will not appear until posted.'
                    }
                ]}
            />
            
            {/* Filters Panel */}
            <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-4">
                <ReportPeriodSelector
                    mode={periodMode}
                    onModeChange={applyPeriodMode}
                    month={filters.month}
                    year={filters.year}
                    onMonthChange={handlePeriodMonthChange}
                    onYearChange={handlePeriodYearChange}
                    asOfDate={filters.date}
                    onAsOfDateChange={handleCustomAsOfDateChange}
                    customMode="single"
                    effectText="Posted asset, liability, and equity balances are shown through the selected as-of date."
                    testId="balance-sheet-period"
                />

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="flex items-end">
                        <label className="flex items-center gap-2 text-sm">
                            <input
                                type="checkbox"
                                checked={withZero}
                                onChange={(e) => setWithZero(e.target.checked)}
                                className="rounded border-slate-300"
                            />
                            Include Zero Balances
                        </label>
                    </div>
                </div>
                
                {/* Buttons */}
                <div className="flex gap-3 pt-2">
                    <Button
                        onClick={handleProcess}
                        disabled={loading}
                        className="bg-[#2F8FCB] hover:bg-[#2577a8]"
                        data-testid="bs-process-btn"
                    >
                        {loading ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                Processing...
                            </>
                        ) : (
                            <>
                                <Play className="w-4 h-4 mr-2" /> Process
                            </>
                        )}
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => window.print()}
                        disabled={!data}
                    >
                        <Printer className="w-4 h-4 mr-2" /> Preview / Print
                    </Button>
                    <Button
                        variant="outline"
                        onClick={handleExportPDF}
                        disabled={!data || exporting}
                        data-testid="export-balance-sheet-pdf-btn"
                    >
                        <Download className="w-4 h-4 mr-2" /> {exporting ? 'Exporting...' : 'Export PDF'}
                    </Button>
                </div>
            </div>
            
            {/* Results */}
            {data && (
                <div className="space-y-4">
                    {/* Report Header */}
                    <div className="bg-[#0F2A44] text-white rounded-lg p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold">Balance Sheet</h3>
                                <p className="text-white/70 text-sm">
                                    {reportType === 'as_of' 
                                        ? `As of ${data.as_of_date}`
                                        : `${months.find(m => m.value === filters.month)?.label} ${filters.year} vs ${
                                            compareType === 'prev_month' ? 'Previous Month' :
                                            compareType === 'dec_last_year' ? `December ${filters.year - 1}` :
                                            `${months.find(m => m.value === filters.month)?.label} ${filters.year - 1}`
                                        }`
                                    }
                                </p>
                            </div>
                            <div className="text-right">
                                <p className="text-xs text-white/50">Generated</p>
                                <p className="text-sm">{new Date(data.generated_at).toLocaleString()}</p>
                            </div>
                        </div>
                    </div>

                    <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-4" data-testid="balance-sheet-where-it-appears">
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                            <div>
                                <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">What changed</p>
                                <p className="mt-1 text-sm text-slate-700">The report was generated from posted GL balances only. No source record was edited.</p>
                            </div>
                            <div>
                                <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Where to find it</p>
                                <p className="mt-1 text-sm text-slate-700">This report tab shows the summary. General Ledger shows the posted evidence behind each balance.</p>
                            </div>
                            <div>
                                <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Report effect</p>
                                <p className="mt-1 text-sm text-slate-700">Matches Trial Balance only when both use the same student scope and as-of date.</p>
                            </div>
                            <div>
                                <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Next safe action</p>
                                <p className="mt-1 text-sm text-slate-700">Trace unusual balances to GL, then open Source Ref before preparing any correction JV.</p>
                            </div>
                        </div>
                    </div>
                    
                    {/* Balance Validation */}
                    {data.is_balanced === false && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                            <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                            <div>
                                <p className="font-semibold text-red-700">Imbalance Detected!</p>
                                <p className="text-sm text-red-600">
                                    Assets ({fmt(data.totals?.total_assets ?? 0)}) != Liabilities + Equity ({fmt(data.totals?.total_liabilities_equity ?? 0)})
                                </p>
                                <p className="text-sm text-red-600">
                                    Difference: {fmt(data.imbalance ?? 0)}
                                </p>
                            </div>
                        </div>
                    )}
                    
                    {data.is_balanced && (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center gap-3">
                            <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0" />
                            <div>
                                <p className="font-semibold text-emerald-700">Balance Sheet is Balanced</p>
                                <p className="text-sm text-emerald-600">
                                    Assets = Liabilities + Equity = {fmt(data.totals?.total_assets ?? 0)}
                                </p>
                            </div>
                        </div>
                    )}
                    
                    {/* Sections */}
                    {renderSection('ASSETS', data.sections?.assets || [], 'bg-blue-600', 'border-blue-200')}
                    {renderSection('LIABILITIES', data.sections?.liabilities || [], 'bg-amber-600', 'border-amber-200')}
                    {renderSection('EQUITY', data.sections?.equity || [], 'bg-purple-600', 'border-purple-200')}

                    <div className="rounded-lg border border-indigo-100 bg-indigo-50 p-4" data-testid="balance-sheet-gl-trace-actions">
                        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                            <div>
                                <p className="font-semibold text-indigo-900">Trace this report in General Ledger</p>
                                <p className="text-sm text-indigo-700">
                                    Open posted ledger evidence for Balance Sheet sections, then use Source Ref to find the original JV, Opening Balance, AR/AP, or Bank Recon evidence.
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => traceSectionInGl('Total Assets', data.sections?.assets || [], data.totals?.total_assets ?? 0)}
                                    disabled={!(data.sections?.assets || []).length}
                                    data-testid="trace-bs-assets-gl"
                                >
                                    <BookOpen className="w-4 h-4 mr-2" /> {(data.sections?.assets || []).length ? 'Assets GL' : 'No Assets GL'}
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => traceSectionInGl('Total Liabilities', data.sections?.liabilities || [], data.totals?.total_liabilities ?? 0)}
                                    disabled={!(data.sections?.liabilities || []).length}
                                    data-testid="trace-bs-liabilities-gl"
                                >
                                    <BookOpen className="w-4 h-4 mr-2" /> {(data.sections?.liabilities || []).length ? 'Liabilities GL' : 'No Liabilities GL'}
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => traceSectionInGl('Total Equity', data.sections?.equity || [], data.totals?.total_equity ?? 0)}
                                    disabled={!(data.sections?.equity || []).length}
                                    data-testid="trace-bs-equity-gl"
                                >
                                    <BookOpen className="w-4 h-4 mr-2" /> {(data.sections?.equity || []).length ? 'Equity GL' : 'No Equity GL'}
                                </Button>
                            </div>
                        </div>
                    </div>
                    
                    {/* Totals Summary */}
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                        <h4 className="font-semibold text-slate-900 mb-3">Summary</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-white rounded-lg p-4 border border-blue-200">
                                <p className="text-sm text-slate-500">Total Assets</p>
                                <p className="text-2xl font-bold text-blue-600">{fmt(data.totals?.total_assets ?? 0)}</p>
                                {reportType === 'comparative' && data.totals?.comparison_total_assets != null && (
                                    <p className="text-xs text-slate-400">Comparison: {fmt(data.totals.comparison_total_assets)}</p>
                                )}
                            </div>
                            <div className="bg-white rounded-lg p-4 border border-amber-200">
                                <p className="text-sm text-slate-500">Total Liabilities</p>
                                <p className="text-2xl font-bold text-amber-600">{fmt(data.totals?.total_liabilities ?? 0)}</p>
                                {reportType === 'comparative' && data.totals?.comparison_total_liabilities != null && (
                                    <p className="text-xs text-slate-400">Comparison: {fmt(data.totals.comparison_total_liabilities)}</p>
                                )}
                            </div>
                            <div className="bg-white rounded-lg p-4 border border-purple-200">
                                <p className="text-sm text-slate-500">Total Equity</p>
                                <p className="text-2xl font-bold text-purple-600">{fmt(data.totals?.total_equity ?? 0)}</p>
                                {reportType === 'comparative' && data.totals?.comparison_total_equity != null && (
                                    <p className="text-xs text-slate-400">Comparison: {fmt(data.totals.comparison_total_equity)}</p>
                                )}
                            </div>
                        </div>
                        <div className="mt-4 pt-4 border-t border-slate-200 flex items-center justify-between">
                            <div>
                                <p className="text-sm text-slate-500">Liabilities + Equity</p>
                                <p className="text-xl font-bold text-slate-900">{fmt(data.totals?.total_liabilities_equity ?? 0)}</p>
                            </div>
                            <div className={`px-4 py-2 rounded-lg ${data.is_balanced ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                {data.is_balanced ? 'Balanced' : 'Imbalanced'}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Empty State */}
            {!data && !loading && (
                <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
                    <FileSpreadsheet className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                    <p className="text-slate-500">Click "Process" to generate Balance Sheet</p>
                    <p className="text-sm text-slate-400 mt-1">Data source: Posted GL entries only</p>
                </div>
            )}
        </div>
    );
}

// ================= INCOME STATEMENT TAB =================


export default BalanceSheetTab;
